import { useContext, useCallback, useMemo } from 'react';
import { BeerContext } from '../BeerContext';
import { SetActiveTabAction, SetHasActiveFiltersAction, SetSelectedBeerAction, ClearSelectedBeerAction, SetFilterBarHeightAction } from '../actions/uiStateActions';
import { UiState, Beer } from '../types';

/**
 * Lightweight hook for checking if a specific beer is selected
 * Optimised to minimise re-renders - only re-renders when this specific beer's selection changes
 */
export function useIsSelected(beerId: string) {
  const context = useContext(BeerContext);
  
  if (context === null) {
    throw new Error('useIsSelected must be used within a BeerProvider');
  }
  
  const { state, dispatch } = context;
  const isSelected = state.ui.selectedBeerId === beerId;
  
  const selectBeer = useCallback((id: string | null) => {
    dispatch(new SetSelectedBeerAction(id));
  }, [dispatch]);
  
  return { isSelected, selectBeer };
}

/**
 * Hook for accessing and manipulating UI state
 * Following Interface Segregation Principle by providing a focused API
 */
export function useUiState() {
  const context = useContext(BeerContext);
  
  if (context === null) {
    throw new Error('useUiState must be used within a BeerProvider');
  }
  
  const { state, dispatch } = context;
  
  // UI state selectors
  const ui: UiState = state.ui;
  
  // Get the selected beer object from the ID
  const selectedBeer: Beer | null = useMemo(() => {
    if (!ui.selectedBeerId) return null;
    return state.beers.find(beer => beer.id === ui.selectedBeerId) || null;
  }, [ui.selectedBeerId, state.beers]);
  
  // UI state action creators
  const setActiveTab = useCallback((tab: string) => {
    dispatch(new SetActiveTabAction(tab));
  }, [dispatch]);
  
  const setHasActiveFilters = useCallback((hasFilters: boolean) => {
    dispatch(new SetHasActiveFiltersAction(hasFilters));
  }, [dispatch]);
  
  const selectBeer = useCallback((beerId: string | null) => {
    dispatch(new SetSelectedBeerAction(beerId));
  }, [dispatch]);
  
  const clearSelection = useCallback(() => {
    dispatch(new ClearSelectedBeerAction());
  }, [dispatch]);
  
  const setFilterBarHeight = useCallback((height: number) => {
    dispatch(new SetFilterBarHeightAction(height));
  }, [dispatch]);
  
  return {
    // State
    ui,
    activeTab: ui.activeTab,
    hasActiveFilters: ui.hasActiveFilters,
    selectedBeerId: ui.selectedBeerId,
    selectedBeer,
    filterBarHeight: ui.filterBarHeight,
    
    // Actions
    setActiveTab,
    setHasActiveFilters,
    selectBeer,
    clearSelection,
    setFilterBarHeight,
  };
}
